import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
// Public platform-highlight feeds — session-free to avoid GoTrue AbortError.
import { supabasePublic as supabase } from "../../../config/supabaseClient";
import {
  parseNewsletterDescriptionHtml,
  resolveNewsletterTitle,
} from "../../../Utils/newsletterDescription";
import { StaticVariables } from "../../../Utils/StaticVariables";
import type { HighlightItem } from "./types";

dayjs.extend(utc);

// Raised from 60 so platform highlight tabs show all of a creator's posts.
// IDs come from get_general_feed_count_content which returns every active post;
// capping too low means only a fraction of the feed is ever shown.
const DEFAULT_POSTS_PER_TAB = 2000;

/** Page size for content-page platform feeds — matches LiB query feeds. */
export const PLATFORM_POSTS_PAGE_SIZE = 25;

export type TemplatePlatformPostsPage = {
  posts: unknown[];
  /** Offset for the next RPC page (`offset_num`). */
  nextOffset: number;
  /** Total rows reported by the RPC (0 when unknown). */
  total: number;
  pageSize: number;
};

export function normalizeTemplatePlatformPostIds(
  postIds: string[] | undefined | null,
): string[] {
  return Array.from(new Set((postIds ?? []).map(String)))
    .map((id) => id.trim())
    .filter(Boolean);
}

/** Map highlight tab keys → LiB `get_content_type_postsspinn_*` params. */
export function resolveHighlightPlatformRpc(platformKey: string): {
  kind: "story" | "type";
  platform: string;
  feedTypes: string[];
} {
  const key = String(platformKey || "")
    .trim()
    .toLowerCase();
  if (key === "instagram_posts") {
    return { kind: "type", platform: "instagram", feedTypes: ["FEED", "REELS"] };
  }
  if (key === "instagram_stories") {
    return { kind: "story", platform: "instagram", feedTypes: ["STORY"] };
  }
  if (key === "linkedin") {
    return { kind: "type", platform: "linkedin", feedTypes: ["POST"] };
  }
  const byPlatform: Record<string, string[]> = {
    youtube: ["VIDEO"],
    tiktok: ["TIKTOK"],
    pinterest: ["PIN"],
    newsletter: ["MAIL"],
    twitter: ["TWEET"],
    podcast: ["PODCAST"],
    facebook: ["FEED", "VIDEO", "REELS"],
    snapchat: ["FEED", "STORY"],
    spotify: ["PODCAST"],
    applepodcast: ["PODCAST"],
  };
  return {
    kind: "type",
    platform: key || "instagram",
    feedTypes: byPlatform[key] ?? (StaticVariables().TYPES as string[]),
  };
}

export function platformKeyToShowSocialsForRpc(platformKey: string): string[] {
  if (platformKey === "instagram_posts" || platformKey === "instagram_stories") {
    return ["instagram"];
  }
  if (platformKey === "MISC-content") return ["MISC-content"];
  return [platformKey];
}

/** Platforms list for `get_custom_content_orderedd` (same family as link-in-bio custom content carousels). */
export function platformKeyToContentPlatformsForRpc(platformKey: string): string[] {
  const contentPlatforms = StaticVariables().CONTENT_PLATFORMS as string[];
  if (platformKey === "instagram_posts" || platformKey === "instagram_stories") {
    return ["instagram"];
  }
  if (contentPlatforms.includes(platformKey)) {
    return [platformKey];
  }
  return contentPlatforms;
}

/** Same filter as `FeedNew.filterShowSocials` — legacy RPC expects the creator's enabled platforms list. */
export function filterTemplateShowSocials(
  showSocials: unknown[] | undefined | null,
): string[] | undefined {
  if (!Array.isArray(showSocials) || showSocials.length === 0) return undefined;
  const excluded = new Set(["LTK", "Amazon", "ShopMy", "MISC"]);
  return showSocials
    .map((s) => String(s).trim())
    .filter((s) => s && !excluded.has(s));
}

export function resolveShowSocialsForPlatformRpc(
  platformKey: string,
  userShowSocials?: unknown[] | null,
): string[] {
  const filtered = filterTemplateShowSocials(userShowSocials);
  if (filtered?.length) return filtered;
  return platformKeyToShowSocialsForRpc(platformKey);
}

export function templatePlatformPostsQueryKey(
  userId: string,
  username: string,
  platformKey: string,
  postIds: string[],
  maxPosts: number = DEFAULT_POSTS_PER_TAB,
): readonly [string, string, string, string, string] {
  const slice = Array.from(new Set(postIds)).slice(0, maxPosts);
  return [
    "template-platform-posts",
    userId,
    username,
    platformKey,
    slice.join(","),
  ] as const;
}

/** Infinite-query key for offset-paginated platform highlight feeds (content pages). */
export function templatePlatformPostsInfiniteQueryKey(
  userId: string,
  username: string,
  platformKey: string,
): readonly [string, string, string, string, number] {
  return [
    "template-platform-posts-offset",
    userId,
    username.trim().toLowerCase(),
    platformKey,
    PLATFORM_POSTS_PAGE_SIZE,
  ] as const;
}

export function getNextTemplatePlatformPostsPageParam(
  lastPage: TemplatePlatformPostsPage,
): number | undefined {
  if (!lastPage) return undefined;
  if (!lastPage.posts.length) return undefined;
  if (lastPage.posts.length < lastPage.pageSize) return undefined;
  if (lastPage.total > 0 && lastPage.nextOffset >= lastPage.total) {
    return undefined;
  }
  return lastPage.nextOffset;
}

function parseContentTypeRpcPayload(content: unknown): {
  posts: unknown[];
  total: number;
} {
  if (Array.isArray(content)) {
    return { posts: content, total: content.length };
  }
  if (content && typeof content === "object") {
    const c = content as { data?: unknown; total?: unknown };
    const posts = Array.isArray(c.data) ? c.data : [];
    const total = Number(c.total ?? 0);
    return { posts, total: Number.isFinite(total) ? total : 0 };
  }
  return { posts: [], total: 0 };
}

async function fetchTemplatePlatformPostsLegacy(args: {
  userId: string;
  username: string;
  platformKey: string;
  postIds: string[];
  maxPosts: number;
  userShowSocials?: unknown[] | null;
}): Promise<unknown[]> {
  const { userId, username, platformKey, postIds, maxPosts, userShowSocials } = args;
  const { data, error } = await supabase.rpc("get_posts_by_ids_content_platformm", {
    post_ids: postIds,
    userid: userId,
    username,
    index_type: "active",
    show_socials: resolveShowSocialsForPlatformRpc(platformKey, userShowSocials),
  });
  if (error) throw error;
  if (!Array.isArray(data)) return [];
  return Array.from(new Map(data.map((item: any) => [item.post_id, item])).values());
}

/**
 * Load a fixed id list (related rail / bulk helpers). Not used for content-page
 * infinite scroll — that uses {@link fetchTemplatePlatformPostsPage}.
 */
export async function fetchTemplatePlatformPostsByIds(args: {
  userId: string;
  username: string;
  platformKey: string;
  postIds: string[];
  maxPosts?: number;
  userShowSocials?: unknown[] | null;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    platformKey,
    postIds,
    maxPosts = DEFAULT_POSTS_PER_TAB,
    userShowSocials,
  } = args;
  const ids = normalizeTemplatePlatformPostIds(postIds).slice(0, maxPosts);
  if (!userId || !username || ids.length === 0) return [];

  const lcUser = username.trim().toLowerCase();
  const platforms = platformKeyToContentPlatformsForRpc(platformKey);

  try {
    const { data: result, error } = await supabase.rpc("get_custom_content_orderedd", {
      userid: userId,
      user_name: lcUser,
      post_ids: ids,
      order_type: "CUSTOM",
      custom_order: ids,
      platforms,
      limit_num: ids.length,
      removed_post_ids: [],
    });

    if (error) throw error;

    const responseData =
      result && typeof result === "object" && "data" in (result as object)
        ? (result as { data?: unknown }).data
        : result;
    const arr = Array.isArray(responseData) ? responseData : [];
    if (arr.length > 0) {
      const deduped = Array.from(
        new Map(
          arr.map((item: Record<string, unknown>) => [
            String(item.post_id ?? item.id ?? "").trim(),
            item,
          ]),
        ).values(),
      ).filter((row) => String((row as { post_id?: unknown }).post_id ?? "").trim());

      if (deduped.length > 0) {
        return enrichTemplateNewsletterPostsMetadata({
          posts: deduped,
          platformKey,
          userId,
          username: lcUser,
          postIds: ids,
          maxPosts: ids.length,
          userShowSocials,
        });
      }
    }
  } catch {
    /* fall through to legacy RPC */
  }

  return fetchTemplatePlatformPostsLegacy({
    userId,
    username: lcUser,
    platformKey,
    postIds: ids,
    maxPosts: ids.length,
    userShowSocials,
  });
}

/**
 * One network page of platform highlight posts via LiB offset RPCs
 * (`get_content_type_postsspinn_new` / story). Does not use the ~100-id list
 * from `get_general_feed_count_content`.
 */
export async function fetchTemplatePlatformPostsPage(args: {
  userId: string;
  username: string;
  platformKey: string;
  /** Unused for offset feeds — kept for call-site compatibility. */
  postIds?: string[];
  offset?: number;
  limit?: number;
  userShowSocials?: unknown[] | null;
}): Promise<TemplatePlatformPostsPage> {
  const {
    userId,
    username,
    platformKey,
    offset = 0,
    limit = PLATFORM_POSTS_PAGE_SIZE,
  } = args;
  const pageSize = Math.max(1, Math.floor(limit) || PLATFORM_POSTS_PAGE_SIZE);
  const start = Math.max(0, Math.floor(offset) || 0);
  const empty: TemplatePlatformPostsPage = {
    posts: [],
    nextOffset: start,
    total: 0,
    pageSize,
  };
  if (!userId || !username || !platformKey) return empty;

  const lcUser = username.trim().toLowerCase();
  const resolved = resolveHighlightPlatformRpc(platformKey);

  try {
    if (resolved.kind === "story") {
      const today = dayjs().utc().format("YYYY-MM-DD");
      const yesterday = dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
      const { data: content, error } = await supabase.rpc(
        "get_storytypecontenttteonord_pin_new",
        {
          userid: userId,
          user_name: lcUser,
          platform_name: resolved.platform,
          feed_type: "STORY",
          start_date: yesterday,
          end_date: today,
          order_type: "DESC",
          custom_order: [],
          limit_num: pageSize,
          offset_num: start,
          pinned_post_ids: [],
          shoppable_status_filter: "all",
        },
      );
      if (error) throw error;
      const parsed = parseContentTypeRpcPayload(content);
      return {
        posts: parsed.posts,
        nextOffset: start + pageSize,
        total: parsed.total,
        pageSize,
      };
    }

    const { data: content, error } = await supabase.rpc(
      "get_content_type_postsspinn_new",
      {
        userid: userId,
        user_name: lcUser,
        platform_name: resolved.platform,
        feed_type: resolved.feedTypes,
        order_type: "DESC",
        custom_order: [],
        limit_num: pageSize,
        offset_num: start,
        pinned_post_ids: [],
        shoppable_status_filter: "all",
      },
    );
    if (error) throw error;
    const parsed = parseContentTypeRpcPayload(content);
    let posts = parsed.posts;
    if (platformKey === "newsletter" && posts.length > 0) {
      const ids = posts
        .map((row) =>
          String(
            (row as { post_id?: unknown }).post_id ??
              (row as { id?: unknown }).id ??
              "",
          ).trim(),
        )
        .filter(Boolean);
      posts = await enrichTemplateNewsletterPostsMetadata({
        posts: posts as Record<string, unknown>[],
        platformKey,
        userId,
        username: lcUser,
        postIds: ids,
        maxPosts: ids.length,
        userShowSocials: args.userShowSocials,
      });
    }
    return {
      posts,
      nextOffset: start + pageSize,
      total: parsed.total,
      pageSize,
    };
  } catch {
    return empty;
  }
}

/**
 * `get_custom_content_orderedd` can omit newsletter `metadata.description`.
 * Merge from `get_posts_by_ids_content_platformm` (same source as search feed tiles).
 */
async function enrichTemplateNewsletterPostsMetadata(args: {
  posts: Record<string, unknown>[];
  platformKey: string;
  userId: string;
  username: string;
  postIds: string[];
  maxPosts: number;
  userShowSocials?: unknown[] | null;
}): Promise<Record<string, unknown>[]> {
  const { posts, platformKey, userId, username, postIds, maxPosts, userShowSocials } =
    args;
  if (platformKey !== "newsletter" || posts.length === 0) {
    return posts;
  }

  try {
    const legacy = (await fetchTemplatePlatformPostsLegacy({
      userId,
      username,
      platformKey,
      postIds,
      maxPosts,
      userShowSocials,
    })) as Record<string, unknown>[];

    const legacyById = new Map<string, Record<string, unknown>>();
    for (const row of legacy) {
      const id = String(row.post_id ?? row.id ?? "").trim();
      if (id) legacyById.set(id, row);
    }

    return posts.map((row) => {
      const id = String(row.post_id ?? row.id ?? "").trim();
      const legacyRow = legacyById.get(id);
      if (!legacyRow) return row;

      const merged: Record<string, unknown> = { ...row };
      if (
        !parseNewsletterDescriptionHtml(merged.metadata) &&
        legacyRow.metadata != null
      ) {
        merged.metadata = legacyRow.metadata;
      }
      if (!merged.caption && legacyRow.caption) merged.caption = legacyRow.caption;
      if (!merged.content_title && legacyRow.content_title) {
        merged.content_title = legacyRow.content_title;
      }
      const mergedTitle = resolveNewsletterTitle(merged);
      const legacyTitle = resolveNewsletterTitle(legacyRow);
      if (legacyTitle !== "Newsletter" && mergedTitle === "Newsletter") {
        merged.caption = legacyRow.caption ?? legacyTitle;
        merged.content_title = legacyRow.content_title ?? legacyTitle;
      }
      if (!merged.media_url && legacyRow.media_url) merged.media_url = legacyRow.media_url;
      if (!merged.post_date && legacyRow.post_date) merged.post_date = legacyRow.post_date;
      return merged;
    });
  } catch {
    return posts;
  }
}

/** Preserve highlight `postIds` order; append any extra rows returned by the RPC. */
export function orderTemplatePlatformPostsByHighlightIds(
  posts: unknown[],
  orderedPostIds: string[],
): unknown[] {
  const arr = Array.isArray(posts) ? (posts as Record<string, unknown>[]) : [];
  if (!orderedPostIds?.length) return [...arr];
  const byId = new Map<string, Record<string, unknown>>();
  for (const p of arr) {
    const k = String((p as { post_id?: unknown }).post_id ?? (p as { id?: unknown }).id ?? "").trim();
    if (k) byId.set(k, p);
  }
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  for (const raw of orderedPostIds) {
    const k = String(raw).trim();
    const row = byId.get(k);
    if (row) {
      out.push(row);
      seen.add(k);
    }
  }
  for (const p of arr) {
    const k = String((p as { post_id?: unknown }).post_id ?? (p as { id?: unknown }).id ?? "").trim();
    if (k && !seen.has(k)) out.push(p);
  }
  return out;
}

/** Shape rows for {@link RenderIndividualUI} (expects `id`, `type`, caption/title fields). */
export function mapPostsForTemplateRenderIndividualUI(posts: unknown[]): Record<string, unknown>[] {
  return (Array.isArray(posts) ? posts : []).map((row) => {
    const r = row as Record<string, unknown>;
    const id = String(r.post_id ?? r.id ?? "").trim();
    const type =
      typeof r.type === "string" && String(r.type).trim() !== "" ? String(r.type) : "FEED";
    const titleText = String(r.content_title ?? r.caption ?? r.product_name ?? "").trim();
    const caption = String(r.caption ?? r.content_title ?? "").trim() || titleText;
    const platform = r.platform ? String(r.platform).toLowerCase() : r.platform;
    return {
      ...r,
      id,
      post_id: id,
      type,
      platform,
      caption: caption || undefined,
      content_title: titleText || caption || undefined,
    };
  });
}

/** Highlights that have a platform key and post IDs (same filter as `TemplatePlatformHighlightFeed`). */
export function templatePlatformHighlightTabs(highlights: HighlightItem[] | undefined) {
  return (highlights ?? []).filter((h) => h.iconKey && (h.postIds?.length ?? 0) > 0);
}
